import { Injectable } from '@angular/core';
import { WorkService } from './work.service';
import {
  catchError,
  combineAll,
  combineLatest,
  filter,
  map,
  shareReplay,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  AuthService,
  LoggerService,
  StateService,
  Unity4Claim,
} from '@adreno-common';
import { UserProfileService } from '@app/core/services';
import { Observable, from, of } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LateToWorkComponent } from '../components';
import { LateShiftReason, Shift, ListShiftAdherencesVm } from '../models';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';

@Injectable({
  providedIn: 'root',
})
export class SchedCheckService extends StateService<State> {
  private existingReasons$ = this.select(
    (state) => state.existingReasons,
    () =>
      this.http
        .get<ListShiftAdherencesVm>(`${this.usersApi}/shift-adherences`)
        .subscribe((vm) => {
          this.state = {
            existingReasons: vm.reasons.map(
              (r) =>
                ({
                  ...r,
                  startUtc: new Date(`${r.startUtc}Z`),
                  finishUtc: new Date(`${r.finishUtc}Z`),
                  recordedUtc: new Date(`${r.recordedUtc}Z`),
                } as LateShiftReason)
            ),
          };
        })
  );

  private usersApi = `${environment.apiPath}/users`;

  constructor(
    private authSvc: AuthService,
    private upSvc: UserProfileService,
    private workSvc: WorkService,
    private modalSvc: NgbModal,
    private http: HttpClient,
    private logger: LoggerService
  ) {
    super({
      existingReasons: undefined,
    });
  }

  private showModal(late: LateShiftReason) {
    this.logger.warn('SchedCheckService', 'showModal', { late })();
    const modal = this.modalSvc.open(LateToWorkComponent, {
      backdrop: 'static',
      keyboard: false,
    });
    modal.componentInstance.late = late;
    return from(modal.result).pipe(
      map((result) => result as LateShiftReason),
      switchMap((late) => this.logReason(late))
    );
  }

  currentShifts$(campaignId: number) {
    const now = new Date();
    return this.workSvc.scheduledCampaigns$.pipe(
      take(1),
      map((scheduledCampaigns) => {
        const campaigns = scheduledCampaigns?.filter(
          (sc) =>
            sc.campaignId == campaignId ||
            (campaignId == 0 && sc.actionType.startsWith('IB'))
        );
        const lates = campaigns?.reduce((result, c) => {
          const shift = c.shifts.find(
            (s) => s.startUtc <= now && s.finishUtc > now
          );
          if (shift) {
            return result.concat({
              ...shift,
              infraction: 'early',
              recordedUtc: new Date(),
              campaignId: c.actionType.startsWith('IB') ? 0 : c.campaignId,
            });
          }
          return result;
        }, [] as LateShiftReason[]);
        return lates;
      })
    );
  }

  lateCheck(campaignId: number) {
    const now = new Date();
    const lateAllowance = 5 * 60 * 1000; //5mins in ms
    return this.authSvc.hasClaim$(Unity4Claim.ScheduleCheck).pipe(
      take(1),
      withLatestFrom(this.upSvc.userProfile$.pipe(filter((up) => !!up))),
      switchMap(([hc, up]) => {
        if (!hc || up.country != 'Australia') return of([undefined, undefined]);

        return this.workSvc.scheduledCampaigns$.pipe(
          take(1),
          map((scheduledCampaigns) => {
            const campaigns = scheduledCampaigns?.filter(
              (sc) =>
                sc.campaignId == campaignId ||
                (campaignId == 0 && sc.actionType.startsWith('IB'))
            );
            const lates = campaigns?.reduce((result, c) => {
              const shift = c.shifts.find(
                (s) => s.startUtc <= now && s.finishUtc > now
              );
              if (shift) {
                return result.concat({
                  ...shift,
                  infraction:
                    now.getTime() - shift.startUtc.getTime() > lateAllowance
                      ? 'late'
                      : 'on-time',
                  recordedUtc: new Date(),
                  campaignId: c.actionType.startsWith('IB') ? 0 : c.campaignId,
                });
              }
              return result;
            }, [] as LateShiftReason[]);
            return lates;
          }),
          switchMap((currentShifts) =>
            this.existingReasons$.pipe(
              filter((er) => !!er),
              take(1),
              map((existingReasons) => [currentShifts, existingReasons])
            )
          )
        );
      }),
      switchMap(([currentShifts, existingReasons]) => {
        if (!currentShifts || currentShifts.length == 0) return of(undefined); //no shifts found - nothing to be done
        if (
          currentShifts.every((cs) =>
            existingReasons?.some(
              (er) =>
                er.campaignId == cs.campaignId &&
                er.startUtc.getTime() == cs.startUtc.getTime() &&
                er.finishUtc.getTime() == cs.finishUtc.getTime()
            )
          )
        ) {
          //already have a reason given
          return of(undefined);
        }
        if (currentShifts.every((cs) => cs.infraction == 'on-time')) {
          //agent is on time - log a reason to prevent future late evaluations when returning from break, etc
          return this.logReason({ ...currentShifts[0], reason: 'on-time' });
        }
        const late = currentShifts.find((cs) => cs.infraction == 'late')!;
        return this.showModal(late);
      })
    );
  }

  logReason(reason: LateShiftReason) {
    return this.http.post(`${this.usersApi}/shift-adherence`, reason).pipe(
      tap(
        () =>
          (this.state = {
            existingReasons: [...(this.state.existingReasons || []), reason],
          })
      ),
      map(() => undefined),
      catchError(() => of(undefined))
    );
  }
}

interface State {
  existingReasons?: LateShiftReason[];
}
